import {
  HsMatrix4,
  HsQuaternion,
  HsVector3,
  type HsGraphicObject,
  type HsHypersceneTypes,
  type HsHypersceneViewer,
} from '@sibe/hyperscene';

export enum GizmoAction {
  None,
  Move,
  Rotate,
  Scale,
}

/** A camera ray through a canvas point, in world space. */
export interface PointerRay {
  readonly origin: HsVector3;
  readonly direction: HsVector3;
}

export interface GizmoHandle {
  readonly object: HsGraphicObject;
  readonly action: GizmoAction;
  /** World axis (0 x, 1 y, 2 z) to move or scale along or rotate around; the plane normal for plane moves. */
  readonly axis: number;
  readonly plane: boolean;
  /** Placement relative to the gizmo center. */
  readonly local: HsMatrix4;
}

const PARALLEL_EPSILON: number = 1e-6;

export function makePointerRay(viewer: HsHypersceneViewer, clientX: number, clientY: number): PointerRay {
  const bounds: DOMRect = viewer.surface.canvas.getBoundingClientRect();
  const x: number = ((clientX - bounds.left) / bounds.width) * 2 - 1;
  const y: number = 1 - ((clientY - bounds.top) / bounds.height) * 2;
  const origin: HsVector3 = HsVector3.makeZeroVector().setFromMatrixPosition(viewer.camera.worldMatrix.values);
  const direction: HsVector3 = new HsVector3(x, y, 0.5)
    .unproject(viewer.camera.projectionMatrixInverse.values, viewer.camera.worldMatrix.values)
    .subtract(origin)
    .normalize();
  return { origin, direction };
}

/** Unit vector along world axis `axis`. */
export function makeAxisVector(axis: number): HsVector3 {
  const vector: HsVector3 = HsVector3.makeZeroVector();
  vector.values[axis] = 1;
  return vector;
}

/** Rotation that turns +Y, the direction of the generated handle geometry, onto world axis `axis`. */
export function makeAxisRotation(axis: number): HsQuaternion {
  if (axis === 0) {
    return HsQuaternion.makeFromAxisAndAngle([0, 0, 1], -Math.PI / 2);
  }
  if (axis === 2) {
    return HsQuaternion.makeFromAxisAndAngle([1, 0, 0], Math.PI / 2);
  }
  return HsQuaternion.makeIdentityQuaternion();
}

/** Parameter of the point on the line `point + s * axis` closest to the ray, or null when they are parallel. */
function closestAxisParameter(point: HsVector3, axis: HsVector3, ray: PointerRay): number | null {
  const offset: HsVector3 = HsVector3.makeCopy(point).subtract(ray.origin);
  const cosine: number = HsVector3.dot(axis, ray.direction);
  const denominator: number = 1 - cosine * cosine;
  if (denominator < PARALLEL_EPSILON) {
    return null;
  }
  return (cosine * HsVector3.dot(ray.direction, offset) - HsVector3.dot(axis, offset)) / denominator;
}

/** Where the ray meets the plane through `point` with `normal`, or null when it misses. */
function intersectPlane(point: HsVector3, normal: HsVector3, ray: PointerRay): HsVector3 | null {
  const denominator: number = HsVector3.dot(normal, ray.direction);
  if (Math.abs(denominator) < PARALLEL_EPSILON) {
    return null;
  }
  const distance: number = HsVector3.dot(normal, HsVector3.makeCopy(point).subtract(ray.origin)) / denominator;
  return distance < 0 ? null : HsVector3.makeCopy(ray.direction).multiplyByScalar(distance).add(ray.origin);
}

/**
 * World-aligned handles around a target object that move, rotate or scale it by dragging. Moves follow the
 * pointer along an axis or across a plane, rotations follow it around an axis, scales follow the ratio of the
 * pointer's distance from the center along an axis.
 */
export class TransformGizmo {
  public readonly handles: GizmoHandle[] = [];

  private readonly center: HsVector3;
  private readonly startCenter: HsVector3 = HsVector3.makeZeroVector();
  private readonly startHit: HsVector3 = HsVector3.makeZeroVector();
  private readonly startTarget: HsMatrix4 = HsMatrix4.makeIdentityMatrix();
  private startParameter: number = 0;
  private dragged: GizmoHandle | null = null;

  constructor(private readonly target: HsGraphicObject, center: HsHypersceneTypes.Array3) {
    this.center = new HsVector3(...center);
  }

  /** Adds `object`, placed around the origin, as a handle and moves it to the gizmo center. */
  public addHandle(object: HsGraphicObject, action: GizmoAction, axis: number, plane: boolean = false): void {
    const handle: GizmoHandle = { object, action, axis, plane, local: HsMatrix4.makeCopy(object.worldMatrix) };
    this.handles.push(handle);
    this.placeHandle(handle);
  }

  public findHandle(object: HsGraphicObject): GizmoHandle | null {
    return this.handles.find((handle: GizmoHandle) => handle.object === object) ?? null;
  }

  /** Starts dragging `handle`; false when the handle has no action or the ray misses it. */
  public beginDrag(handle: GizmoHandle, ray: PointerRay): boolean {
    if (handle.action === GizmoAction.None) {
      return false;
    }
    if (this.usesPlane(handle)) {
      const hit: HsVector3 | null = intersectPlane(this.center, makeAxisVector(handle.axis), ray);
      if (hit === null) {
        return false;
      }
      this.startHit.copy(hit);
    } else {
      const parameter: number | null = closestAxisParameter(this.center, makeAxisVector(handle.axis), ray);
      if (parameter === null) {
        return false;
      }
      this.startParameter = parameter;
    }
    this.startCenter.copy(this.center);
    this.startTarget.copy(this.target.worldMatrix);
    this.dragged = handle;
    return true;
  }

  /** Applies the drag to the pointer ray; returns true when the target changed. */
  public drag(ray: PointerRay): boolean {
    const handle: GizmoHandle | null = this.dragged;
    if (handle === null) {
      return false;
    }
    const axis: HsVector3 = makeAxisVector(handle.axis);
    const changed: boolean = this.usesPlane(handle)
      ? this.dragOnPlane(handle, axis, intersectPlane(this.startCenter, axis, ray))
      : this.dragAlongAxis(handle, axis, closestAxisParameter(this.startCenter, axis, ray));
    if (changed) {
      this.target.updateBoundingSphere();
      this.handles.forEach((other: GizmoHandle) => this.placeHandle(other));
    }
    return changed;
  }

  public endDrag(): void {
    this.dragged = null;
  }

  private usesPlane(handle: GizmoHandle): boolean {
    return handle.action === GizmoAction.Rotate || (handle.action === GizmoAction.Move && handle.plane);
  }

  private dragOnPlane(handle: GizmoHandle, axis: HsVector3, hit: HsVector3 | null): boolean {
    if (hit === null) {
      return false;
    }
    if (handle.action === GizmoAction.Move) {
      this.center.copy(this.startCenter).add(hit).subtract(this.startHit);
      this.target.worldMatrix.setPosition(this.center.values);
      return true;
    }
    const from: HsVector3 = HsVector3.makeCopy(this.startHit).subtract(this.startCenter);
    const to: HsVector3 = HsVector3.makeCopy(hit).subtract(this.startCenter);
    const angle: number = Math.atan2(HsVector3.dot(axis, HsVector3.makeCopy(from).cross(to)), HsVector3.dot(from, to));
    const rotation: HsMatrix4 = HsMatrix4.compose(
      [0, 0, 0],
      HsQuaternion.makeFromAxisAndAngle(axis.values, angle).values,
      [1, 1, 1]
    );
    this.target.worldMatrix
      .copy(this.startTarget)
      .setPosition([0, 0, 0])
      .premultiply(rotation)
      .setPosition(this.startCenter.values);
    return true;
  }

  private dragAlongAxis(handle: GizmoHandle, axis: HsVector3, parameter: number | null): boolean {
    if (parameter === null) {
      return false;
    }
    if (handle.action === GizmoAction.Move) {
      this.center
        .copy(axis)
        .multiplyByScalar(parameter - this.startParameter)
        .add(this.startCenter);
      this.target.worldMatrix.setPosition(this.center.values);
      return true;
    }
    // Grabbing a scale handle at the center would divide by almost zero.
    if (Math.abs(this.startParameter) < 0.01) {
      return false;
    }
    const scale: HsHypersceneTypes.Array3 = [1, 1, 1];
    scale[handle.axis] = Math.max(0.1, parameter / this.startParameter);
    this.target.worldMatrix.copy(this.startTarget).scale(scale);
    return true;
  }

  private placeHandle(handle: GizmoHandle): void {
    handle.object.worldMatrix.copy(handle.local).addTransition(this.center.values);
    handle.object.updateBoundingSphere();
  }
}
